<template>
  <v-main>
    <v-container fluid>
      <!-- Combined Header and Filtration Section -->
      <v-row class="mb-2">
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Page Header -->
            <page-header
              :title="isDeletePage() != true ? 'Coupon Management' : 'Deleted Coupons'"
              :subtitle="isDeletePage() != true ? 'Manage and monitor all discount coupons' : 'Restore or permanently delete coupons'"
            >
              <template v-slot:actions>
                <div v-if="isDeletePage() != true" class="d-flex justify-end align-center">
                    <v-btn
                      color="info"
                      outlined
                      small
                      to="/coupons/usage-report"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-chart-bar</v-icon>
                      Usage Report
                    </v-btn>
                    <v-btn
                      color="primary"
                      elevation="1"
                      small
                      to="/coupons/create"
                      class="mr-2 px-3"
                    >
                      <v-icon left x-small>mdi-plus</v-icon>
                      Add Coupon
                    </v-btn>
                  </div>
              </template>
            </page-header>

            <!-- Filtration Section -->
            <v-card-subtitle class="pb-1 pt-2 px-4">
              <v-row class="w-100 ma-0" align="center">
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-filter-variant</v-icon
                    >
                    <span class="text-body-2 font-weight-medium"
                      >Filter Coupons</span
                    >
                  </div>
                </v-col>
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <v-chip
                      v-if="hasActiveFilters"
                      color="info"
                      x-small
                      class="mr-2"
                    >
                      <v-icon left x-small>mdi-filter</v-icon>
                      {{ getActiveFiltersCount() }} Active
                    </v-chip>
                    <v-btn
                      text
                      x-small
                      color="error"
                      @click="clearAllFilters"
                      :disabled="!hasActiveFilters"
                    >
                      <v-icon left x-small>mdi-filter-remove</v-icon>
                      Clear
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-subtitle>

            <v-card-text class="px-4 pb-4">
              <v-row>
                <!-- Search Field -->
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="filter.search_keyword"
                    label="Search Coupons"
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    placeholder="Code, description..."
                    hide-details
                    @click:clear="
                      (filter.search_keyword = null), (page = 1), getData()
                    "
                    @keydown.enter="(page = 1), getData()"
                  ></v-text-field>
                </v-col>

                <!-- Discount Type Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.discount_type"
                    :items="discountTypes"
                    label="Discount Type"
                    prepend-inner-icon="mdi-percent"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Position Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.position_id"
                    :items="positionFilterItems"
                    item-text="name"
                    item-value="id"
                    label="Applicable For"
                    prepend-inner-icon="mdi-account-badge"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>

                <!-- Status Filter -->
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    prepend-inner-icon="mdi-toggle-switch"
                    outlined
                    dense
                    clearable
                    hide-details
                    @change="(page = 1), getData()"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Coupons Table Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3">
            <!-- Card Header with Title and Controls -->
            <v-card-title class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Title Section -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <v-icon class="mr-2" color="primary" size="20"
                      >mdi-table</v-icon
                    >
                    <span class="text-body-1 font-weight-medium"
                      >Coupons List</span
                    >
                  </div>
                </v-col>

                <!-- Results Summary Section -->
                <v-col cols="12" md="5" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-chip color="primary" outlined x-small class="mr-2">
                      <v-icon left x-small>mdi-ticket-percent</v-icon>
                      {{ count }} {{ count === 1 ? "Coupon" : "Coupons" }}
                    </v-chip>
                    <span class="text-caption grey--text" v-if="count > 0">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Controls Section -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Items per page selector -->
                    <div class="d-flex align-center mr-2">
                      <span class="text-caption grey--text mr-1">Show:</span>
                      <v-btn-toggle
                        v-model="selectedItemsPerPage"
                        dense
                        class="mr-2"
                        @change="changeItemsPerPageToggle"
                      >
                        <v-btn x-small outlined :value="10">10</v-btn>
                        <v-btn x-small outlined :value="25">25</v-btn>
                        <v-btn x-small outlined :value="50">50</v-btn>
                      </v-btn-toggle>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>

            <!-- Table Content -->
            <v-card-text class="pa-0">
              <!-- Loading State -->
              <section
                v-if="isLoading"
                style="height: 400px"
                class="d-flex align-center justify-center"
              >
                <div class="text-center">
                  <health-loader message="Loading coupons..." />
                </div>
              </section>

              <!-- Data Table -->
              <v-data-table
                v-else
                :headers="headers"
                :items="coupons"
                :server-items-length="count"
                :page.sync="page"
                :items-per-page="itemsPerPage"
                hide-default-footer
                class="enhanced-table"
              >
                <!-- Serial Number Column -->
                <template #[`item.sr_no`]="{ index }">
                  <span class="font-weight-medium primary--text">
                    {{ (page - 1) * itemsPerPage + index + 1 }}
                  </span>
                </template>

                <!-- Coupon Info Column -->
                <template #[`item.coupon_info`]="{ item }">
                  <div class="d-flex align-center">
                    <v-avatar size="32" class="mr-2" color="primary">
                      <v-icon color="white" size="18">mdi-ticket-percent</v-icon>
                    </v-avatar>
                    <div>
                      <div class="text-body-2 font-weight-bold">
                        {{ item.code || "No code" }}
                      </div>
                      <div class="text-caption grey--text">
                        {{ truncateText(item.description, 40) || "No description" }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Discount Column -->
                <template #[`item.discount_info`]="{ item }">
                  <div>
                    <status-chip :color="item.discount_type === 'percentage' ? 'blue' : 'purple'" :label="String(item.discount_type === 'percentage' ? item.discount_value + '%' : '₹' + parseFloat(item.discount_value || 0).toFixed(2))" />
                    <div class="text-caption grey--text mt-1" v-if="item.min_order_amount">
                      Min: ₹{{ item.min_order_amount | toFixed2 }}
                    </div>
                  </div>
                </template>

                <!-- Validity Column -->
                <template #[`item.validity`]="{ item }">
                  <div class="text-caption">
                    <div v-if="item.valid_from">
                      <strong>From:</strong> {{ formatDate(item.valid_from) }}
                    </div>
                    <div v-if="item.valid_until">
                      <strong>Until:</strong> {{ formatDate(item.valid_until) }}
                    </div>
                    <v-chip
                      x-small
                      :color="isCouponExpired(item) ? 'error' : 'success'"
                      dark
                      class="mt-1"
                    >
                      {{ isCouponExpired(item) ? 'Expired' : 'Valid' }}
                    </v-chip>
                  </div>
                </template>

                <!-- Usage Column -->
                <template #[`item.usage`]="{ item }">
                  <div class="text-caption">
                    <div>Used: {{ item.used_count || 0 }}</div>
                    <div v-if="item.usage_limit">Limit: {{ item.usage_limit }}</div>
                    <div v-else>Limit: Unlimited</div>
                  </div>
                </template>

                <!-- Position Column -->
                <template #[`item.position`]="{ item }">
                  <div>
                    <v-chip x-small :color="item.is_global ? 'success' : 'primary'" dark class="mb-1">
                      <v-icon left x-small>{{ item.is_global ? 'mdi-earth' : 'mdi-account-multiple' }}</v-icon>
                      {{ item.is_global ? 'Global' : 'Specific' }}
                    </v-chip>
                    <div v-if="item.position">
                      <v-chip x-small color="indigo" dark>
                        <v-icon left x-small>mdi-account-badge</v-icon>
                        {{ item.position.name }}
                      </v-chip>
                    </div>
                  </div>
                </template>

                <!-- Status Column -->
                <template #[`item.is_active`]="{ item }">
                  <v-chip
                    :color="item.is_active ? 'success' : 'error'"
                    small
                    dark
                  >
                    <v-icon left x-small>
                      {{
                        item.is_active ? "mdi-check-circle" : "mdi-close-circle"
                      }}
                    </v-icon>
                    {{ item.is_active ? "Active" : "Inactive" }}
                  </v-chip>
                </template>

                <!-- Actions Column -->
                <template #[`item.actions`]="{ item }">
                  <div class="d-flex justify-center">
                    <template v-if="!isDeletePage()">
                      <!-- Edit Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="primary"
                            :to="`/coupons/${encryptIt(item.id)}`"
                            class="mr-1"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-pencil</v-icon>
                          </v-btn>
                        </template>
                        <span>Edit Coupon</span>
                      </v-tooltip>

                      <!-- Delete Button -->
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="error"
                            @click="showDeleteConfirmation(item)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete Coupon</span>
                      </v-tooltip>
                    </template>

                    <template v-else>
                      <v-tooltip bottom>
                        <template #activator="{ on, attrs }">
                          <v-btn
                            x-small
                            color="success"
                            @click="Restore_this(item.id)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon x-small left>mdi-restore</v-icon>
                            Restore
                          </v-btn>
                        </template>
                        <span>Restore Coupon</span>
                      </v-tooltip>
                    </template>
                  </div>
                </template>

                <!-- Empty State -->
                <template #no-data>
                  <div class="text-center py-12">
                    <v-icon size="80" color="grey lighten-3"
                      >mdi-ticket-percent-outline</v-icon
                    >
                    <h3 class="text-h6 grey--text mt-4 mb-2">No Coupons Found</h3>
                    <p class="grey--text mb-4">
                      {{
                        hasActiveFilters
                          ? "No coupons match your current filters. Try adjusting your search criteria."
                          : "You haven't added any coupons yet."
                      }}
                    </p>
                    <v-btn
                      v-if="!hasActiveFilters && !isDeletePage()"
                      color="primary"
                      to="/coupons/create"
                    >
                      <v-icon left>mdi-plus</v-icon>
                      Add Your First Coupon
                    </v-btn>
                    <v-btn
                      v-else-if="hasActiveFilters"
                      color="primary"
                      outlined
                      @click="clearAllFilters"
                    >
                      <v-icon left>mdi-filter-remove</v-icon>
                      Clear Filters
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Integrated Pagination Footer -->
            <v-divider></v-divider>
            <v-card-actions class="py-2 px-4">
              <v-row class="w-100 ma-0" align="center" dense>
                <!-- Pagination Info -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center">
                    <span class="text-caption grey--text">
                      {{ (page - 1) * itemsPerPage + 1 }}-{{
                        Math.min(page * itemsPerPage, count)
                      }}
                      of {{ count }}
                    </span>
                  </div>
                </v-col>

                <!-- Pagination Controls -->
                <v-col cols="12" md="6" class="pa-0">
                  <div class="d-flex align-center justify-center">
                    <v-btn
                      x-small
                      outlined
                      :disabled="page <= 1"
                      @click="page--"
                      class="mr-2"
                    >
                      <v-icon x-small left>mdi-chevron-left</v-icon>
                      Prev
                    </v-btn>

                    <v-pagination
                      v-model="page"
                      :length="paginationLength"
                      :total-visible="5"
                      class="compact-pagination mx-1"
                    ></v-pagination>

                    <v-btn
                      x-small
                      outlined
                      :disabled="page >= paginationLength"
                      @click="page++"
                      class="ml-2"
                    >
                      Next
                      <v-icon x-small right>mdi-chevron-right</v-icon>
                    </v-btn>
                  </div>
                </v-col>

                <!-- Jump to Page -->
                <v-col cols="12" md="3" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <span class="text-caption grey--text mr-2">Go to:</span>
                    <v-text-field
                      v-model="jumpToPage"
                      type="number"
                      :min="1"
                      :max="paginationLength"
                      dense
                      outlined
                      hide-details
                      hide-spin-buttons
                      class="no-spin-arrows compact-input"
                      style="max-width: 60px"
                      @keydown.enter="goToPage"
                      @blur="goToPage"
                    ></v-text-field>
                  </div>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <!-- Delete Success Dialog -->
      <v-dialog v-model="is_deleted_coupon" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-empty
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Coupon deleted successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_deleted_coupon = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Restore Success Dialog -->
      <v-dialog v-model="is_restored_coupon" width="600">
        <v-card>
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon
                  large
                  style="color: green; font-size: 90px; width: 200px !important"
                >
                  mdi-delete-restore
                </v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Coupon restored successfully.</h3>
                      <br />
                      <v-btn color="success" @click="is_restored_coupon = false">
                        OK
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500" persistent>
        <v-card>
          <v-card-title class="headline error white--text d-flex align-center">
            <v-icon color="white" class="mr-2">mdi-alert-circle</v-icon>
            Confirm Delete
          </v-card-title>

          <v-card-text class="pt-4">
            <div class="text-center">
              <v-icon size="64" color="error" class="mb-3"
                >mdi-delete-alert</v-icon
              >
              <h3 class="mb-3">Are you sure you want to delete this coupon?</h3>

              <div
                class="pa-3 ma-2"
                style="background: #f5f5f5; border-radius: 8px"
              >
                <div class="d-flex align-center">
                  <v-avatar size="48" class="mr-3" color="primary">
                    <v-icon color="white" size="24">mdi-ticket-percent</v-icon>
                  </v-avatar>
                  <div class="text-left">
                    <div class="font-weight-bold text-body-1">
                      {{ couponToDelete ? couponToDelete.code : "" }}
                    </div>
                    <div class="text-caption grey--text">
                      {{
                        couponToDelete
                          ? truncateText(couponToDelete.description, 50)
                          : ""
                      }}
                    </div>
                  </div>
                </div>
              </div>

              <p class="text-body-2 grey--text mt-3 mb-1">
                <strong>Warning:</strong> This action will move the coupon to the
                trash.
              </p>
              <p class="text-body-2 grey--text">
                You can restore it later from the deleted coupons section.
              </p>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-spacer></v-spacer>
            <v-btn
              color="grey darken-1"
              text
              @click="cancelDelete"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-close</v-icon>
              Cancel
            </v-btn>
            <v-btn
              color="error"
              @click="confirmDelete"
              :loading="isDeleting"
              :disabled="isDeleting"
            >
              <v-icon left>mdi-delete</v-icon>
              Delete Coupon
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CouponsPage",
  data() {
    return {
      filter: {
        search_keyword: "",
        is_active: "",
        discount_type: "",
        position_id: null,
        show_deleted: false,
      },
      positions: [],
      page: 1,
      itemsPerPage: 10,
      selectedItemsPerPage: 10,
      paginationLength: 0,
      count: 0,
      jumpToPage: 1,
      is_deleted_coupon: false,
      is_restored_coupon: false,
      showDeleteDialog: false,
      couponToDelete: null,
      isDeleting: false,
      coupons: [],
      statuses: [
        { text: "Active", value: "1" },
        { text: "Inactive", value: "0" },
      ],
      discountTypes: [
        { text: "Percentage", value: "percentage" },
        { text: "Fixed Amount", value: "fixed" },
      ],
      headers: [
        {
          text: "#",
          value: "sr_no",
          sortable: false,
          width: "50px",
          align: "center",
        },
        {
          text: "Coupon Info",
          value: "coupon_info",
          sortable: false,
          width: "220px",
        },
        {
          text: "Discount",
          value: "discount_info",
          sortable: false,
          width: "140px",
        },
        {
          text: "Validity",
          value: "validity",
          sortable: false,
          width: "160px",
        },
        {
          text: "Usage",
          value: "usage",
          sortable: false,
          width: "100px",
        },
        {
          text: "Applicable For",
          value: "position",
          sortable: false,
          width: "120px",
        },
        {
          text: "Status",
          value: "is_active",
          sortable: false,
          width: "90px",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          width: "110px",
          align: "center",
        },
      ],
      isLoading: false,
    };
  },

  computed: {
    positionFilterItems() {
      return this.positions;
    },
    hasActiveFilters() {
      return (
        this.filter.search_keyword ||
        this.filter.is_active ||
        this.filter.discount_type ||
        this.filter.position_id
      );
    },
  },

  watch: {
    page: {
      handler: function (newPage) {
        this.jumpToPage = newPage;
        this.getData();
      },
    },
    itemsPerPage: "getData",
    $route() {
      this.getData();
    },
  },

  mounted() {
    this.jumpToPage = this.page;
    this.fetchPositions();
    this.getData();
  },

  methods: {
    async fetchPositions() {
      try {
        const res = await axios.get("/coupons/dropdown");
        if (res.data.success) {
          this.positions = res.data.data.positions || [];
        }
      } catch (e) {
        console.error(e);
      }
    },

    async getData() {
      this.filter.show_deleted = this.$route.query.open_trash ? 1 : 0;
      this.isLoading = true;

      this.filter.search_keyword = this.filter.search_keyword || "";
      this.filter.is_active = this.filter.is_active || "";
      this.filter.discount_type = this.filter.discount_type || "";

      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.itemsPerPage +
        "&show_deleted=" +
        this.filter.show_deleted +
        "&is_active=" +
        this.filter.is_active +
        "&discount_type=" +
        this.filter.discount_type +
        "&search_keyword=" +
        this.filter.search_keyword;

      if (this.filter.position_id) {
        query += "&position_id=" + this.filter.position_id;
      }

      await axios
        .get(`coupons?${query}`)
        .then((response) => {
          this.coupons = response.data.data;
          this.count = response.data.count;
          this.paginationLength = Math.ceil(this.count / this.itemsPerPage);
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
      this.isLoading = false;
    },

    showDeleteConfirmation(coupon) {
      this.couponToDelete = coupon;
      this.showDeleteDialog = true;
    },

    cancelDelete() {
      this.showDeleteDialog = false;
      this.couponToDelete = null;
      this.isDeleting = false;
    },

    async confirmDelete() {
      if (!this.couponToDelete) return;

      this.isDeleting = true;

      await axios
        .post(`/coupons/delete/${this.encryptIt(this.couponToDelete.id)}`)
        .then(() => {
          this.showDeleteDialog = false;
          this.couponToDelete = null;
          this.isDeleting = false;
          this.is_deleted_coupon = true;
          this.getData();
        })
        .catch((error) => {
          console.log(error);
          this.isDeleting = false;
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    async Restore_this(id) {
      await axios
        .post(`/coupons/restore/${this.encryptIt(id)}`)
        .then(() => {
          this.is_restored_coupon = true;
          this.getData();
        })
        .catch(function (error) {
          console.log(error);
          if (error.response && error.response.status === 401) {
            window.location.href = "/auth/login";
          }
        });
    },

    isCouponExpired(coupon) {
      if (!coupon.valid_until) return false;
      return new Date(coupon.valid_until) < new Date();
    },

    isDeletePage() {
      return this.$route.query.open_trash ? true : false;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + "...";
    },

    clearAllFilters() {
      this.filter.search_keyword = "";
      this.filter.is_active = "";
      this.filter.discount_type = "";
      this.filter.position_id = null;
      this.page = 1;
      this.getData();
    },

    getActiveFiltersCount() {
      let count = 0;
      if (this.filter.search_keyword) count++;
      if (this.filter.is_active) count++;
      if (this.filter.discount_type) count++;
      if (this.filter.position_id) count++;
      return count;
    },

    changeItemsPerPageToggle() {
      this.itemsPerPage = this.selectedItemsPerPage;
      this.page = 1;
      this.getData();
    },

    goToPage() {
      const targetPage = parseInt(this.jumpToPage);
      if (
        targetPage &&
        targetPage >= 1 &&
        targetPage <= this.paginationLength
      ) {
        this.page = targetPage;
      } else {
        this.jumpToPage = this.page;
      }
    },
  },
};
</script>

<style scoped>
.v-container {
  max-width: 100% !important;
}

.enhanced-table >>> .v-data-table__wrapper {
  border-radius: 8px;
  font-size: 0.75rem !important;
}

.enhanced-table >>> .v-data-table-header {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  font-weight: 600;
}

.enhanced-table >>> .v-data-table-header th {
  padding: 8px 6px !important;
  border-bottom: 1px solid #e0e0e0 !important;
  font-size: 0.75rem !important;
  height: 36px !important;
}

.enhanced-table >>> tbody tr:hover {
  background: rgba(25, 118, 210, 0.04) !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody tr {
  height: 44px !important;
  transition: all 0.1s ease;
}

.enhanced-table >>> tbody td {
  padding: 6px 6px !important;
  vertical-align: middle !important;
  border-bottom: 1px solid #f5f5f5 !important;
  font-size: 0.75rem !important;
  height: 44px !important;
}

.v-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
}

.v-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.v-btn {
  text-transform: none !important;
  font-weight: 500;
  letter-spacing: 0.2px;
  font-size: 0.75rem !important;
  min-height: 28px !important;
}

.v-btn--x-small {
  height: 24px !important;
  min-width: 48px !important;
  font-size: 0.7rem !important;
  padding: 0 8px !important;
}

.v-btn--small {
  height: 28px !important;
  min-width: 56px !important;
  font-size: 0.75rem !important;
  padding: 0 12px !important;
}

.v-btn-toggle >>> .v-btn {
  min-width: 32px !important;
  height: 24px !important;
  font-size: 0.7rem !important;
  padding: 0 6px !important;
}

.compact-pagination >>> .v-pagination__item {
  height: 28px !important;
  min-width: 32px !important;
  width: auto !important;
  padding: 0 6px !important;
  font-size: 0.75rem !important;
  margin: 0 1px !important;
}

.compact-pagination >>> .v-pagination__navigation {
  height: 28px !important;
  width: 28px !important;
  min-width: 28px !important;
}

.compact-input >>> .v-input__control .v-input__slot {
  min-height: 24px !important;
}

.compact-input >>> .v-input__control .v-input__slot input {
  padding: 2px 6px !important;
  font-size: 0.7rem !important;
}

.v-chip {
  font-weight: 500;
  letter-spacing: 0.1px;
  font-size: 0.7rem !important;
  height: 20px !important;
}

.v-chip--x-small {
  height: 18px !important;
  font-size: 0.65rem !important;
}

.v-chip .v-icon {
  font-size: 12px !important;
}

.v-avatar {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.v-progress-circular {
  filter: drop-shadow(0 2px 4px rgba(25, 118, 210, 0.3));
}

@media (max-width: 768px) {
  .enhanced-table >>> tbody td {
    padding: 4px 4px !important;
    font-size: 0.7rem !important;
  }

  .enhanced-table >>> .v-data-table-header th {
    padding: 6px 4px !important;
    font-size: 0.7rem !important;
  }
}

.no-spin-arrows >>> input[type="number"]::-webkit-outer-spin-button,
.no-spin-arrows >>> input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.no-spin-arrows >>> input[type="number"] {
  -moz-appearance: textfield;
}
</style>
